
/**
 * Provides context for device inflection (mobile/desktop) and breakpoint information.
 * This allows components to adapt their layout and behavior based on the current device.
 *
 * @module core/providers/InflectionProvider/InflectionProvider
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import { getCurrentBreakpoint, isMobileDisplay, isServer } from '../../utils/device_detection/device_detection';
import * as deviceDetection from '../../utils/device_detection/device_detection';

/**
 * Inflection provider component.
 * Wraps its children and provides inflection and breakpoint information via context.
 *
 * @param {InflectionProviderProps} props - Component props.
 * @param {React.ReactNode} props.children - Child components to be rendered within the provider's scope.
 * @param {('Desktop'|'Mobile')} [props.baseInflection='Desktop'] - Initial inflection state. Determines the starting device type.
 * @returns {JSX.Element} The InflectionProvider component.
 *
 * @typedef {object} InflectionProviderProps
 * @property {React.ReactNode} children - Child components to be rendered within the provider's scope
 * @property {('Desktop'|'Mobile')} [baseInflection='Desktop'] - Initial inflection state
 *
 * @example
 * <InflectionProvider baseInflection="Mobile">
 *   <App />
 * </InflectionProvider>
 */
export const InflectionProvider = ( { children, baseInflection } ) => {
  /**
     * State for the current inflection.
     */
  const [inflection, setInflection] = useState( baseInflection || defaultProps.inflection.toUpperCase() );
  /**
     * State for the current breakpoint.
     */
  const [breakpoint, setBreakpoint] = useState( getCurrentBreakpoint( baseInflection ) );

  /**
 * State for the window dimensions.
 */
  const [windowSize, setWindowSize] = useState( {
    width: !isServer() ? window.innerWidth : 0,
    height: !isServer() ? window.innerHeight : 0
  } );
  /**
     * Ref for the active timer.
     */
  const resizeTimer = useRef( null );

  /**
     * Memoized inflection response object.
     */
  const inflectionResponse = useMemo( () => getInflectionResponse( inflection ), [inflection] );

  /**
     * Memoized breakpoint response object.
     */
  const breakpointResponse = useMemo( () => getBreakpointResponse( breakpoint ), [breakpoint] );

  /**
     * Updates the inflection, breakpoint and dimension based on device detection.
     *
     * @param {string} currentInflection - The current inflection value.
     * @param {string} currentBreakpoint - The current breakpoint value.
     */
  const updateInflection = useCallback( ( currentInflection, currentBreakpoint ) => {
    const newInflection = deviceDetection.getDeviceInflection( isMobileDisplay() );
    const newBreakpoint = deviceDetection.getCurrentBreakpoint();

    if( currentInflection !== newInflection ){
      setInflection( newInflection.toUpperCase() );
    }

    if( currentBreakpoint !== newBreakpoint ){
      setBreakpoint( newBreakpoint.toUpperCase() );
    }

    !isServer() && setWindowSize( { width: window.innerWidth, height: window.innerHeight } );
  }, [] );

  useEffect( () => {
    updateInflection( inflection, breakpoint );
  }, [updateInflection] );

  /**
     * Debounced resize handler to prevent excessive updates.
     */
  const debouncedHandleResize = useCallback( () => {
    if( resizeTimer.current ){
      clearTimeout( resizeTimer.current );
    }
    resizeTimer.current = setTimeout( () => {
      updateInflection( inflection, breakpoint );
    }, 100 );
  }, [inflection, breakpoint, updateInflection] );

  useEffect( () => {
    global.addEventListener( 'resize', debouncedHandleResize );
    return () => global.removeEventListener( 'resize', debouncedHandleResize );
  }, [debouncedHandleResize] );

  /**
     * Memoized context value.
     */
  const contextValue = useMemo( () => ( {
    inflection: inflectionResponse,
    breakpoint: breakpointResponse,
    windowSize
  } ), [inflectionResponse, breakpointResponse, windowSize] );


  return (
    <InflectionContext.Provider value={ contextValue }>
      { children }
    </InflectionContext.Provider>
  );
};

/**
 * PropTypes for InflectionProvider.
 * @type {object}
 */
export const propTypes = {
  inflection: PropTypes.string,
  children: PropTypes.node.isRequired // Added children as required
};

/**
 * Default props for InflectionProvider.
 * @type {object}
 */
export const defaultProps = {
  inflection: 'Desktop'
};

/**
 * Creates an inflection response object.
 *
 * @param {string} [inflection=''] The current inflection.
 * @returns {object} An object indicating mobile/desktop state.
 */
export const getInflectionResponse = ( inflection = '' ) => ( {
  MOBILE: inflection?.toUpperCase() === 'MOBILE',
  DESKTOP: inflection?.toUpperCase() === 'DESKTOP',
  TYPE: inflection?.toUpperCase() === 'MOBILE' ? 'mobile' : 'desktop'
} );

/**
 * Creates a breakpoint response object.
 *
 * @param {string} [inputBreakpoint=''] The current breakpoint.
 * @returns {object} An object with breakpoint information and helper functions.
 */
export const getBreakpointResponse = ( inputBreakpoint = '' ) => {
  const breakpoint = inputBreakpoint.toUpperCase();
  return {
    BREAKPOINTS: initialState.BREAKPOINTS,
    CURRENT_BREAKPOINT: breakpoint,
    isSmallDevice: () => breakpoint === 'SM',
    isMediumDevice: () => breakpoint === 'MD',
    isLargeDevice: () => breakpoint === 'LG' || breakpoint === 'XL'
  };
};

/**
 * Initial state for the inflection context.
 * @type {object}
 * @property {boolean} MOBILE - Initial mobile inflection state.
 * @property {boolean} DESKTOP - Initial desktop inflection state.
 * @property {Array<string>} BREAKPOINTS - Supported breakpoints.
 * @property {string} CURRENT_BREAKPOINT - Initial breakpoint.
 */
export const initialState = {
  MOBILE: false,
  DESKTOP: false,
  BREAKPOINTS: ['SM', 'MD', 'LG', 'XL'],
  CURRENT_BREAKPOINT: ''
};

/**
 * Context for device inflection and breakpoint.
 */
const InflectionContext = createContext( initialState );

/**
 * Hook to access device inflection and breakpoint information.
 *
 * @returns {DeviceInflectionResult} An object containing inflection and breakpoint data.
 *
 * @typedef {object} DeviceInflectionResult
 * @property {InflectionData} inflection - Device inflection information
 * @property {BreakpointData} breakpoint - Breakpoint information
 *
 * @typedef {object} InflectionData
 * @property {boolean} MOBILE - True if current device is mobile
 * @property {boolean} DESKTOP - True if current device is desktop
 * @property {('mobile'|'desktop')} TYPE - String representation of device type
 *
 * @typedef {object} BreakpointData
 * @property {string[]} BREAKPOINTS - Array of available breakpoints ['SM', 'MD', 'LG', 'XL']
 * @property {('SM'|'MD'|'LG'|'XL')} CURRENT_BREAKPOINT - Current active breakpoint
 * @property {() => boolean} isSmallDevice - Function returning true if current breakpoint is SM
 * @property {() => boolean} isMediumDevice - Function returning true if current breakpoint is MD
 * @property {() => boolean} isLargeDevice - Function returning true if current breakpoint is LG or XL
 *
 * @example
 * const { inflection, breakpoint } = useDeviceInflection();
 * const isMobile = inflection.MOBILE;
 * const isLarge = breakpoint.isLargeDevice();
 */
export const useDeviceInflection = () => useContext( InflectionContext );

InflectionProvider.propTypes = propTypes;
InflectionProvider.defaultProps = defaultProps;

export default InflectionProvider;
